import React from "react";
import { Link } from "react-router";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowUpRight, ShieldCheck, Lock, Sparkles } from "lucide-react";
import Images from "../../utils/Images";

const floatingTransition = {
  duration: 9,
  repeat: Infinity,
  repeatType: "mirror",
  ease: "easeInOut",
};

const AuthSplitLayout = ({
  title,
  subtitle,
  children,
  footer,
  backTo = "/",
  sideBadge = "INFOBOXX ACCESS",
  sideTitle = "Get answers. Make informed decisions.",
  sideDescription = "Ghana's most trusted platform for real estate, legal, health, nutrition, careers, and betting guidance.",
  sideBullets = [],
  contentMaxW = "max-w-2xl",
  /** Solid white left column (e.g. registration) instead of cream. */
  whiteLeftPanel = false,
  /**
   * Drop the inner “card” so the form sits on a continuous white surface and can use nearly full column width.
   * Implied when `whiteLeftPanel` is true unless you pass `flushForm={false}` explicitly.
   */
  flushForm,
}) => {
  const flush = flushForm ?? whiteLeftPanel;
  const leftBg = whiteLeftPanel ? "bg-white" : "bg-[#f7f4ec]";
  const headerBtnClass = whiteLeftPanel
    ? "inline-flex items-center gap-2 rounded-full border border-gray-200 bg-slate-50 px-3 py-1.5 text-sm font-medium text-gray-700 shadow-sm transition hover:border-[#FFD221]/60 hover:bg-white hover:text-black"
    : "inline-flex items-center gap-2 rounded-full border border-gray-200 bg-white px-3 py-1.5 text-sm font-medium text-gray-700 shadow-sm transition hover:border-[#FFD221]/60 hover:text-black";
  const logoWrapClass = whiteLeftPanel
    ? "inline-flex items-center gap-2 rounded-2xl border border-gray-100 bg-slate-50 px-3 py-2 text-sm font-black shadow-sm"
    : "inline-flex items-center gap-2 rounded-2xl bg-white px-3 py-2 text-sm font-black shadow-sm";

  return (
    <div className="h-screen w-full overflow-hidden bg-[#f7f4ec]">
      <div className="grid h-full w-full lg:grid-cols-[minmax(0,1fr)_minmax(400px,42%)]">

        {/* ── Left: form area ──────────────────────────────────── */}
        <section className={`relative h-full overflow-y-auto ${leftBg}`}>
          <div
            className={[
              "mx-auto flex min-h-full w-full flex-col py-8",
              whiteLeftPanel
                ? "max-w-none px-5 sm:px-8 lg:px-10 xl:px-14 2xl:px-16"
                : `px-5 sm:px-8 lg:px-12 ${contentMaxW}`,
            ].join(" ")}
          >
            <div className="mb-8 flex items-center justify-between">
              <Link to={backTo} className={headerBtnClass}>
                <ArrowLeft size={15} />
                Back home
              </Link>
              <Link to="/" className={logoWrapClass}>
                <img
                  src={Images.infoboxx_logo}
                  alt="Infoboxx"
                  className="h-8 w-auto object-contain"
                  onError={(e) => { e.target.style.display = "none"; }}
                />
              </Link>
            </div>

            <motion.div
              initial={{ opacity: 0, y: 18 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, ease: "easeOut" }}
              className={
                flush
                  ? "rounded-none border-0 bg-transparent p-0 shadow-none"
                  : "rounded-3xl border border-white/80 bg-white p-5 shadow-xl shadow-gray-900/5 sm:p-8"
              }
            >
              <h1 className="text-3xl font-black tracking-tight text-gray-900 sm:text-4xl">{title}</h1>
              {subtitle ? (
                <p className="mt-2 text-sm leading-relaxed text-gray-500 sm:text-base">{subtitle}</p>
              ) : null}
              <div className={subtitle ? "mt-7" : "mt-5"}>{children}</div>
            </motion.div>

            {footer ? (
              <div
                className={
                  whiteLeftPanel
                    ? "mt-8 text-center text-sm text-gray-500"
                    : "mt-5 text-center text-sm text-gray-500"
                }
              >
                {footer}
              </div>
            ) : null}
          </div>
        </section>

        {/* ── Right: brand panel ───────────────────────────────── */}
        <aside className="relative hidden h-full overflow-hidden lg:block">
          <div className="absolute inset-0 bg-[#f7f4ec]" />

          {/* Yellow glow accents */}
          <motion.div
            className="absolute -left-20 top-20 h-64 w-64 rounded-full blur-3xl"
            style={{ background: "rgba(255,210,33,0.32)" }}
            animate={{ x: [0, 30, 0], y: [0, -18, 0] }}
            transition={{ ...floatingTransition, duration: 10 }}
          />
          <motion.div
            className="absolute -right-16 bottom-20 h-80 w-80 rounded-full blur-3xl"
            style={{ background: "rgba(255,255,255,0.9)" }}
            animate={{ x: [0, -22, 0], y: [0, 22, 0] }}
            transition={floatingTransition}
          />

          {/* Rotating rings */}
          <motion.div
            className="absolute left-1/2 top-1/2 h-[460px] w-[460px] -translate-x-1/2 -translate-y-1/2 rounded-full"
            style={{ border: "1px solid rgba(0,0,0,0.08)" }}
            animate={{ rotate: 360 }}
            transition={{ duration: 48, repeat: Infinity, ease: "linear" }}
          />
          <motion.div
            className="absolute left-1/2 top-1/2 h-[320px] w-[320px] -translate-x-1/2 -translate-y-1/2 rounded-full"
            style={{ border: "1px solid rgba(0,0,0,0.1)" }}
            animate={{ rotate: -360 }}
            transition={{ duration: 30, repeat: Infinity, ease: "linear" }}
          />

          <div className="relative z-10 flex h-full flex-col justify-between p-8 xl:p-10">
            {/* Logo top */}
            <div className="flex items-center justify-end">
              <Link
                to="/"
                className="inline-flex items-center gap-2.5 rounded-full border border-black/10 bg-white px-4 py-2 text-sm font-bold text-black shadow-xl transition hover:bg-[#817e71]"
              >
                <img
                  src={Images.infoboxx_logo}
                  alt="Infoboxx"
                  className="h-7 w-auto object-contain"
                  onError={(e) => { e.target.style.display = "none"; }}
                />
                <ArrowUpRight size={14} />
              </Link>
            </div>

            {/* Main panel */}
            <div className="flex flex-1 flex-col items-center justify-center gap-8">
              <motion.p
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: 0.2 }}
                className="max-w-sm text-center text-sm font-medium leading-relaxed"
                style={{ color: "rgba(0,0,0,0.55)" }}
              >
                Ghana's most-trusted information and expert connection platform
              </motion.p>

              <motion.div
                initial={{ opacity: 0, x: 24 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.55, delay: 0.1, ease: "easeOut" }}
                className="w-full max-w-md rounded-3xl p-7 shadow-2xl"
                style={{ background: "rgba(255,255,255,0.86)", border: "1px solid rgba(0,0,0,0.08)", backdropFilter: "blur(12px)" }}
              >
                <p className="text-xs font-bold uppercase tracking-[0.2em]" style={{ color: "#c9a000" }}>
                  {sideBadge}
                </p>
                <h2 className="mt-3 text-2xl font-black leading-tight text-black">{sideTitle}</h2>
                <p className="mt-3 text-sm leading-relaxed" style={{ color: "rgba(0,0,0,0.65)" }}>
                  {sideDescription}
                </p>
                {!!sideBullets?.length && (
                  <ul className="mt-5 space-y-2.5">
                    {sideBullets.map((item) => (
                      <li key={item} className="flex items-start gap-2.5 text-sm" style={{ color: "rgba(0,0,0,0.78)" }}>
                        <span
                          className="mt-1.5 inline-block h-1.5 w-1.5 shrink-0 rounded-full"
                          style={{ background: "#FFD221" }}
                        />
                        {item}
                      </li>
                    ))}
                  </ul>
                )}
              </motion.div>

              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.4, delay: 0.35 }}
                className="flex flex-wrap items-center justify-center gap-6 rounded-2xl px-5 py-4"
                style={{ background: "rgba(255,255,255,0.8)", border: "1px solid rgba(0,0,0,0.08)" }}
              >
                {[
                  { Icon: ShieldCheck, label: "Secure" },
                  { Icon: Lock, label: "Verified" },
                  { Icon: Sparkles, label: "Free" },
                ].map(({ Icon, label }) => (
                  <span key={label} className="flex items-center gap-2 text-xs font-semibold" style={{ color: "rgba(0,0,0,0.7)" }}>
                    <Icon size={15} style={{ color: "#FFD221" }} />
                    {label}
                  </span>
                ))}
              </motion.div>
            </div>
          </div>
        </aside>
      </div>
    </div>
  );
};

export default AuthSplitLayout;
